// Route-level states: app-wide not-found, region not-found, the role gate of a whole screen, shell skeleton while /me and /jurisdictions load, and shell or route errors.
import { useQuery } from "@tanstack/react-query";
import { Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { MapPinOffIcon, SearchXIcon } from "lucide-react";
import type { ReactNode } from "react";

import { metaListJurisdictionsOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import type { AccessLevel } from "@/app/nav";
import { defaultRegion, regionParam } from "@/app/region";
import { EmptyState } from "@/components/common/EmptyState";
import { ProblemState } from "@/components/common/ProblemState";
import { RequireRole, RoleRequired } from "@/components/common/RequireRole";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export function NotFoundPage() {
  return (
    <main id="main" className="grid min-h-dvh place-items-center bg-background p-6">
      <Card className="w-[min(520px,100%)]">
        <EmptyState
          icon={SearchXIcon}
          title="Page not found"
          description="The link may be out of date, or the page has moved."
          action={
            <Button asChild variant="outline">
              <Link to="/">Go to the start page</Link>
            </Button>
          }
        />
      </Card>
    </main>
  );
}

export function RegionNotFound() {
  const { data } = useQuery(metaListJurisdictionsOptions());
  const fallback = data === undefined ? undefined : defaultRegion(data);
  return (
    <div className="mx-auto max-w-[1440px] px-7 pt-6 pb-16">
      <Card>
        <EmptyState
          icon={MapPinOffIcon}
          title="Region not found"
          description="No grid code is loaded for this region, or the link is wrong."
          action={
            fallback ? (
              <Button asChild variant="outline">
                <Link to="/$region" params={{ region: regionParam(fallback) }}>
                  Go to {fallback.name}
                </Link>
              </Button>
            ) : null
          }
        />
      </Card>
    </div>
  );
}

interface RestrictedRouteProps {
  level: AccessLevel;
  description: string;
  children: ReactNode;
}

export function RestrictedRoute({ level, description, children }: RestrictedRouteProps) {
  return (
    <RequireRole
      level={level}
      fallback={
        <div className="mx-auto max-w-[1440px] px-7 pt-6 pb-16">
          <Card>
            <RoleRequired level={level} description={description} />
          </Card>
        </div>
      }
    >
      {children}
    </RequireRole>
  );
}

export function ShellPending() {
  return (
    <div
      className="grid h-dvh grid-cols-[var(--sidebar-width-icon)_minmax(0,1fr)] 3xl:grid-cols-[var(--sidebar-width)_minmax(0,1fr)]"
      aria-busy="true"
    >
      <div className="sidebar-gradient" />
      <div className="flex min-w-0 flex-col">
        <div className="flex h-14 items-center gap-4 border-b glass px-5">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="ml-auto h-9 w-[min(420px,38vw)]" />
        </div>
        <div className="mx-auto w-full max-w-[1440px] px-7 pt-6">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="mt-3 h-7 w-72" />
          <div className="mt-5 grid grid-cols-2 gap-3.5 xl:grid-cols-4">
            {["a", "b", "c", "d"].map((key) => (
              <Skeleton key={key} className="h-[106px] rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ShellError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <main id="main" className="grid min-h-dvh place-items-center bg-background p-6">
      <Card className="w-[min(560px,100%)]">
        <ProblemState
          problem={error}
          retry={() => {
            reset();
            void router.invalidate();
          }}
        />
      </Card>
    </main>
  );
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="mx-auto max-w-[1440px] px-7 pt-6 pb-16">
      <Card>
        <ProblemState
          problem={error}
          retry={() => {
            reset();
            void router.invalidate();
          }}
        />
      </Card>
    </div>
  );
}
